/**
 * Finding the best part of an owner's uploaded track, so videos start the
 * music on its hook rather than its intro — what the fixed skip does for the
 * library tracks, measured per file instead.
 */

/** How much of a track a short-form video typically plays. */
export const BEST_PART_WINDOW_SEC = 12;
const STEP_SEC = 0.5;

/**
 * The start (seconds) of the most energetic `windowSec` stretch of a mono
 * signal: RMS per half-second, then the window with the highest mean loudness.
 * Tracks no longer than the window start at 0.
 */
export function findBestMusicStart(
  samples: Float32Array,
  sampleRate: number,
  windowSec: number = BEST_PART_WINDOW_SEC
): number {
  const durationSec = samples.length / sampleRate;
  if (durationSec <= windowSec + STEP_SEC) return 0;

  const stepSamples = Math.max(1, Math.round(STEP_SEC * sampleRate));
  const rms: number[] = [];
  for (let start = 0; start < samples.length; start += stepSamples) {
    const end = Math.min(samples.length, start + stepSamples);
    let sum = 0;
    for (let i = start; i < end; i++) sum += samples[i] * samples[i];
    rms.push(Math.sqrt(sum / Math.max(1, end - start)));
  }

  const windowSteps = Math.max(1, Math.round(windowSec / STEP_SEC));
  const lastStart = rms.length - windowSteps;
  if (lastStart <= 0) return 0;

  let windowSum = 0;
  for (let i = 0; i < windowSteps; i++) windowSum += rms[i];
  let bestSum = windowSum;
  let bestStep = 0;
  for (let step = 1; step <= lastStart; step++) {
    windowSum += rms[step + windowSteps - 1] - rms[step - 1];
    // Strictly louder only, so a flat track keeps its natural start.
    if (windowSum > bestSum * 1.001) {
      bestSum = windowSum;
      bestStep = step;
    }
  }
  return Math.round(bestStep * STEP_SEC * 10) / 10;
}

/**
 * Decodes an audio file in the browser and returns its best start and exact
 * length, or undefined when the file can't be decoded (the render then uses
 * its default start).
 */
export async function detectBestMusicStart(
  file: File
): Promise<{ startSec: number; durationSec: number } | undefined> {
  const AudioContextCtor =
    typeof window !== 'undefined'
      ? (window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext)
      : undefined;
  if (!AudioContextCtor) return undefined;
  const context = new AudioContextCtor();
  try {
    const buffer = await context.decodeAudioData(await file.arrayBuffer());
    const mono = new Float32Array(buffer.length);
    for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
      const data = buffer.getChannelData(ch);
      for (let i = 0; i < data.length; i++) {
        mono[i] += data[i] / buffer.numberOfChannels;
      }
    }
    return {
      startSec: findBestMusicStart(mono, buffer.sampleRate),
      durationSec: buffer.duration,
    };
  } catch {
    return undefined;
  } finally {
    void context.close();
  }
}
